import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import queryString from 'query-string';

import useEffectOnce from '../../../js/hooks/useEffectOnce';
import { getFontFamily, getTitleFontFamily } from '../../../js/utils/sectionHelper';

import SectionWrapper from '../../common/SectionWrapper';

import validateNationalIdSE from '../../../js/utils/national-id-unsub-validation';
import validateNationalIdFI from '../../../js/validators/FI/nationalId';
import validateNationalIdNO from '../../../js/validators/NO/nationalId';
import { useUnsubscribeFormPostMutation } from '../../../js/store/reducers/api-call';

import { brand, region } from '../../../js/localizations/current-locale';
import sizes from '../../../js/styles/Sizes';
import devices from '../../../js/styles/Devices';
import style from './style';

const Wrapper = styled(SectionWrapper)`
  && {
    padding-top: 0rem;
    padding-bottom: 2rem !important;
    display: flex;
    flex-direction: row;
    text-decoration: none;
    text-align: center;
    margin-top: 5rem;
  }
`;

const Container = styled.div`
  && {
    background-color: var(--white);
    padding: 2rem;
    margin-top: 2rem;
    max-width: 40rem;
    margin: 2rem auto 0;
  }
`;

const Heading = styled.h1`
  && {
    font-size: var(--h1-desktop-font-size);
    font-family: var(--title-font-family);
    letter-spacing: var(--has-letter-spacing);
    -webkit-font-smoothing: antialiased;

    @media ${devices.downFromTablet} {
      font-size: var(--h1-mobile-font-size);
    }
  }
`;

const Input = styled.input`
  && {
    width: 75%;
    border-width: 2px;
    border-style: solid;
    border-color: ${(props) => props.borderColor} !important;
    outline-color: ${(props) => props.borderColor} !important;
    border-radius: ${sizes.defaultBorderRadius};
    padding: 0.75rem;
    text-align: center;
    margin-top: 0.5rem;
    font-family: var(--font-family);

    :focus-visible {
      border-color: ${(props) => props.borderColor} !important;
      outline-color: ${(props) => props.borderColor} !important;
    }
  }
`;

const Description = styled.span`
  && {
    font-family: var(--font-family);
  }
`;

const RedLine = styled.div`
  && {
    margin: 0 auto;
    width: 110px;
    height: 3px;
    background-color: #ff3926;
    margin-bottom: 1rem;
    -webkit-animation: enter .5s cubic-bezier(.25,.46,.45,.94) both;
    animation: enter .5s cubic-bezier(.25,.46,.45,.94) both;
    -webkit-animation-delay: 1s;
    animation-delay: 1s;
  }
`;

const Button = styled.button`
  && {
    margin: 2em auto 0 auto;
    border: 1px solid var(--border-grey);
    width: 100%;
    font-size: 0.9rem;
    height: 50px;
    border-radius: 5px;
    cursor: pointer;
    font-weight: bold;
    color: var(--white);
    background: ${(props) => ((props.disabled) ? style.backgroundDisabled : props.backgroundEnabled)} !important;
    border-color: ${(props) => ((props.disabled) ? style.borderColorDisabled : '')};
    pointer-events: ${(props) => ((props.disabled) ? 'none' : '')};
    font-family: var(--font-family);
  }
`;

const UnsubscribePost = ({
  acf,
}) => {
  const [nationalId, setNationalId] = useState();
  const [search, setSearchParam] = useState('');

  const [onUnsubscribeFormPost, {
    isLoading: submitting,
    isSuccess: submitSuccess,
    isError: submitFailed,
  }] = useUnsubscribeFormPostMutation();

  useEffectOnce(() => {
    setSearchParam(window.location.search);
  });

  const queryParams = {
    ...(queryString.parse(search || '')),
  };

  const { cID } = queryParams;

  let validateNationalId;

  switch (region.toUpperCase()) {
    case 'FI':
      validateNationalId = validateNationalIdFI;
      break;
    case 'NO':
      validateNationalId = validateNationalIdNO;
      break;
    case 'SE':
      validateNationalId = validateNationalIdSE;
      break;
    default:
      validateNationalId = validateNationalIdSE;
  }

/* eslint-disable */
  const handleClick = (nationalId, cID) => {
    onUnsubscribeFormPost({nationalId, cID});
  };
  /* eslint-disable */

  const handleChange = (e) => {
    const updatedNationalId = e.target.value;
    setNationalId(updatedNationalId);
  };

  useEffect(() => {
    if (submitFailed) {
      window.location.href = '/unsubscribe-failed';
    } else if (submitSuccess) {
      window.location.href = '/unsubscribe-success';
    }
  }, [submitSuccess, submitFailed])

  const SubmitButton = () => {
    if (validateNationalId(nationalId)) {
      return (
        <Button
          disabled
          backgroundEnabled={style.backgroundEnabledValidatedPost}
          fontFamily={getFontFamily(acf.sectionSettings.fontFamily)}

        >
            {acf.submitButtonText}
        </Button>
      )
    } else {
      return (
        <Button
          onClick={() => handleClick(nationalId, cID)} disabled={submitting}
          backgroundEnabled={style.backgroundEnabled}
          fontFamily={getFontFamily(acf.sectionSettings.fontFamily)}
        >
          {acf.submitButtonText}
        </Button>
      )
    }
  }

  return (acf &&
    <Wrapper
      settings={acf.sectionSettings}
    >
      <Container>
        {acf.title && (
          <Heading
          fontFamily={getTitleFontFamily(acf.sectionSettings.fontFamily)}
          >
            {acf.title}
          </Heading>
        )}
        {brand === 'sambla' && (
          <RedLine />
        )}
        {acf.description &&
          <Description
            fontFamily={getFontFamily(acf.sectionSettings.fontFamily)}
          >
            {acf.description}
          </Description>
        }

        <div
          data-tooltip={acf.tooltip}
          data-tooltip-trigger="hover"
          data-tooltip-width="medium"
          data-tooltip-position="top"
        >
          <Input
            type="text"
            placeholder={acf.inputPlaceholder ? acf.inputPlaceholder : ''}
            onChange={handleChange}
            fontFamily={getFontFamily(acf.sectionSettings.fontFamily)}
            borderColor={!validateNationalId(nationalId) ? 'var(--green)': 'var(--off-white)'}
          />
        </div>
        <SubmitButton />
      </Container>
    </Wrapper>
  );
};

UnsubscribePost.propTypes = {
  onUnsubscribeFormPost: PropTypes.func,
  submitting: PropTypes.bool,
  submitSuccess: PropTypes.bool,
  acf: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    tooltip: PropTypes.string.isRequired,
    inputPlaceholder: PropTypes.string,
    submitButtonText: PropTypes.string.isRequired,
    sectionSettings: SectionWrapper.propTypes.settings,
  }).isRequired,
};

UnsubscribePost.defaultProps = {
  onUnsubscribeFormPost: () => {},
  submitting: false,
  submitSuccess: null,
};

export default UnsubscribePost;
